import { Fragment, useEffect, useState, useTransition } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/card/BackLink";
import { useScreenLabel } from "@/lib/screenTrail";
import { Check, Field, HelpTip, SaveBar, SectionTitle } from "@/components/card/CardParts";
import { AddressMap } from "@/features/addresses/AddressMap";
import { AddressSearch } from "@/features/addresses/AddressSearch";
import {
  addressKeys,
  getAddressLookupStatus,
  type AddressCandidate,
  type AddressCheckResult,
  type AddressField,
  type PlaceHoursQuery,
} from "@/features/addresses/addresses.api";
import {
  addAlias,
  checkLocationAddress,
  locationsKeys,
  mergeLocation,
  removeAlias,
  removeLocationAttachment,
  saveOpeningHours,
  updateLocation,
  uploadLocationAttachment,
  ATTACHMENT_MAX_BYTES,
  type AttachmentFile,
  type AttachmentKind,
  type LocationDetail,
  type PreNotification,
} from "./locations.api";
import { ADDRESS_REQUIREMENTS, LOCATION_REQUIREMENTS, REQUIREMENT_LABEL_KEYS, requirementLabelKey } from "./requirements";
import { describeOpeningHours, type OpeningHours } from "./openingHours";
import { OpeningHoursEditor, PROBLEM_KEYS, draftFromHours, hoursFromDraft, useHoursWords, type HoursDraft } from "./OpeningHoursEditor";
import { HereHoursHint } from "./HereHoursHint";
import { SOURCE_LABEL_KEYS, VERDICT, formatAddress, formatDate } from "./labels";
import { formatBytes } from "@/lib/format";

// The location card (Organisaties → Locaties → one place): the address with its check
// and map, contact, what the on-board computer shows the driver, opening hours,
// requirements, and the aliases the place goes by (docs/tms-stamdata.md §6). Every tab
// saves on its own.

export const LOCATION_CARD_TABS = ["general", "hours", "requirements", "aliases"] as const;
export type LocationCardTab = (typeof LOCATION_CARD_TABS)[number];

const TAB_LABEL_KEYS: Record<LocationCardTab, string> = {
  general: "locations.tabGeneral",
  hours: "locations.tabHours",
  requirements: "locations.tabRequirements",
  aliases: "locations.tabAliases",
};

const ADDRESS_FIELD_LABEL_KEYS: Record<AddressField, string> = {
  street: "locations.streetLabel",
  houseNumber: "locations.houseNumberLabel",
  postalCode: "locations.postalCodeLabel",
  city: "locations.cityLabel",
  countryCode: "locations.countryLabel",
};

const ALIAS_KIND_LABEL_KEYS: Record<LocationDetail["aliases"][number]["kind"], string> = {
  code: "locations.aliasKindCode",
  address: "locations.aliasKindAddress",
  text: "locations.aliasKindText",
};

// A tab keeps to a form's width, so a field such as the postal code does not run
// across half a wide screen (S37).
const CARD_STYLE: React.CSSProperties = { padding: "20px 24px", maxWidth: "64rem" };

// The general tab in six columns: street, number and addition on one row; postal
// code, city and country on the next; the rest in halves and thirds.
const FORM_GRID: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: "16px" };

function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

// Saving one tab: the saved card goes straight into the cache; the lists are
// marked stale and refresh when they are next shown.
function useCardSave(locationId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function save(action: () => Promise<LocationDetail>) {
    setError(null);
    setSaved(false);
    startTransition(async () => {
      try {
        const next = await action();
        queryClient.setQueryData(locationsKeys.detail(locationId), next);
        void queryClient.invalidateQueries({ queryKey: locationsKeys.all, refetchType: "none" });
        setSaved(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.errorGeneric"));
      }
    });
  }

  return { isPending, error, saved, save, setError };
}

function TalisNote() {
  const { t } = useTranslation();
  return (
    <div className="wl-alert wl-alert--info" style={{ marginBottom: "16px" }}>
      <span>{t("locations.talisNote")}</span>
    </div>
  );
}

// ── Algemeen ──────────────────────────────────────────────────────────────────

function AddressCheck({
  location,
  canEdit,
  tookOver,
  onTakeOver,
}: {
  location: LocationDetail;
  canEdit: boolean;
  tookOver: boolean;
  onTakeOver: (candidate: AddressCandidate) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: addressKeys.status, queryFn: getAddressLookupStatus, staleTime: 5 * 60_000 });
  const available = status.data?.available ?? false;
  const [isPending, startTransition] = useTransition();
  const [check, setCheck] = useState<AddressCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const verdict = location.addressVerdict;

  function run() {
    setError(null);
    startTransition(async () => {
      try {
        const answer = await checkLocationAddress(location.id);
        setCheck(answer.check);
        queryClient.setQueryData(locationsKeys.detail(location.id), answer.location);
        void queryClient.invalidateQueries({ queryKey: locationsKeys.all, refetchType: "none" });
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.errorGeneric"));
      }
    });
  }

  const fieldList = (fields: AddressField[]) => fields.map((field) => String(t(ADDRESS_FIELD_LABEL_KEYS[field] as never)).toLowerCase()).join(", ");

  return (
    <div style={{ padding: "12px 16px", background: "var(--surface-subtle)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)" }}>
      <div className="wl-hstack" style={{ gap: "10px", flexWrap: "wrap" }}>
        <span className="t-body-sm" style={{ fontWeight: "var(--fw-medium)" }}>{t("locations.addressCheckTitle")}</span>
        {verdict ? (
          <span className={`wl-status ${VERDICT[verdict].className}`}>
            <span className="wl-status__dot" />
            {t(VERDICT[verdict].labelKey as never)}
          </span>
        ) : (
          <span className="t-caption t-muted">{t("locations.verdictNone")}</span>
        )}
        {location.addressCheckedAt && (
          <span className="t-caption t-muted">{t("locations.checkedOn", { date: formatDate(location.addressCheckedAt) })}</span>
        )}
        <span className="wl-chip">{t(SOURCE_LABEL_KEYS[location.addressSource] as never)}</span>
        <span className="wl-spacer" />
        {canEdit && available && (
          <button type="button" className="wl-btn wl-btn--secondary wl-btn--sm" disabled={isPending} onClick={run}>
            {t("locations.addressCheck")}
          </button>
        )}
        {status.data && !available && <span className="t-caption t-muted">{t("addresses.lookupOff")}</span>}
      </div>
      {error && (
        <div className="wl-alert wl-alert--danger" style={{ marginTop: "10px" }} role="alert">
          <span>{error}</span>
        </div>
      )}
      {check && (
        <div style={{ marginTop: "10px" }}>
          {check.suggestion ? (
            <div className="wl-hstack" style={{ gap: "12px", flexWrap: "wrap", alignItems: "flex-start" }}>
              <div>
                <div className="t-body-sm">{check.suggestion.label}</div>
                {check.differences.length > 0 && (
                  <div className="t-caption t-muted">{t("locations.checkDiffers", { fields: fieldList(check.differences) })}</div>
                )}
                {check.completions.length > 0 && (
                  <div className="t-caption t-muted">{t("locations.checkCompletes", { fields: fieldList(check.completions) })}</div>
                )}
              </div>
              <span className="wl-spacer" />
              {canEdit && (check.differences.length > 0 || check.completions.length > 0) && (
                <button
                  type="button"
                  className="wl-btn wl-btn--secondary wl-btn--sm"
                  disabled={tookOver}
                  onClick={() => check.suggestion && onTakeOver(check.suggestion)}
                >
                  {t("locations.checkTakeOver")}
                </button>
              )}
            </div>
          ) : (
            <div className="t-body-sm t-muted">{t("locations.checkNothing")}</div>
          )}
          {check.suggestion && check.suggestion.lat !== null && check.suggestion.lng !== null && (
            <div style={{ marginTop: "10px" }}>
              <AddressMap lat={check.suggestion.lat} lng={check.suggestion.lng} label={check.suggestion.label} />
            </div>
          )}
        </div>
      )}
      {tookOver && <div className="t-caption" style={{ marginTop: "8px" }}>{t("locations.checkTakenOver")}</div>}
    </div>
  );
}

const ADDRESS_FORM_KEYS = ["street", "houseNumber", "houseNumberAddition", "postalCode", "city", "countryCode"] as const;

const WEB_LINK = /^https?:\/\/\S+$/i;

/** What a file may be: WLX reads the kind from the file itself and refuses anything else (S65). */
const ATTACHMENT_ACCEPT = "application/pdf,video/mp4,video/quicktime,video/webm,image/jpeg,image/png,.pdf,.mp4,.mov,.webm,.jpg,.jpeg,.png";

// "Bijlage adres" and "Bijlage route" (S29): what the on-board computer opens when the
// driver presses Aankomst, and Navigeren (S37). A file — a pdf or a film — that WLX keeps
// and opens through a link of its own without a login (S65), or a link pasted or dropped
// from the browser. A file is put and removed at once; a link goes with the form. Only an
// admin changes them, like the pop-ups (BL3).
function AttachmentField({
  id,
  locationId,
  kind,
  label,
  info,
  help,
  value,
  file,
  disabled,
  onChange,
}: {
  id: string;
  locationId: string;
  kind: AttachmentKind;
  label: string;
  /** What the attachment does, behind the "?". */
  info: string;
  help: string;
  value: string;
  /** The file WLX keeps here, or null when the field is a link or empty. */
  file: AttachmentFile | null;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<"upload" | "remove" | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const link = value.trim();
  const valid = WEB_LINK.test(link);
  const invalid = !file && link !== "" && !valid;

  async function run(work: "upload" | "remove", action: () => Promise<string>) {
    setBusy(work);
    setProblem(null);
    try {
      onChange(await action());
      // The card shows the file once the location is read again.
      await queryClient.invalidateQueries({ queryKey: locationsKeys.detail(locationId) });
    } catch (err) {
      setProblem(err instanceof Error ? err.message : t("common.errorGeneric"));
    } finally {
      setBusy(null);
    }
  }

  function put(chosen: File | undefined) {
    if (!chosen || disabled || busy) return;
    if (chosen.size > ATTACHMENT_MAX_BYTES) {
      setProblem(t("locations.attachmentTooLarge"));
      return;
    }
    void run("upload", async () => (await uploadLocationAttachment(locationId, kind, chosen)).link);
  }

  function remove() {
    if (!file || disabled || busy) return;
    if (!window.confirm(t("locations.attachmentRemoveConfirm", { name: file.fileName }))) return;
    void run("remove", async () => {
      await removeLocationAttachment(locationId, kind);
      return "";
    });
  }

  return (
    <div className="wl-field">
      <div className="wl-hstack" style={{ gap: "4px", alignItems: "center" }}>
        <label className="wl-field__label" htmlFor={id}>{label}</label>
        <HelpTip text={info} />
      </div>
      <div
        className="wl-hstack"
        style={{ gap: "8px" }}
        // A file dropped on the field is put at once; a link dropped on it fills it (below).
        onDragOver={(e) => {
          if (!disabled && e.dataTransfer.types.includes("Files")) e.preventDefault();
        }}
        onDrop={(e) => {
          if (disabled || e.dataTransfer.files.length === 0) return;
          e.preventDefault();
          put(e.dataTransfer.files[0]);
        }}
      >
        {file ? (
          <input id={id} className="wl-input" readOnly value={`${file.fileName} · ${formatBytes(file.fileSize)}`} />
        ) : (
          <input
            id={id}
            className="wl-input"
            type="url"
            inputMode="url"
            maxLength={2000}
            placeholder={disabled ? undefined : t("locations.attachmentPlaceholder")}
            value={value}
            disabled={disabled || busy !== null}
            aria-invalid={invalid || undefined}
            style={invalid ? { borderColor: "var(--border-danger)" } : undefined}
            onChange={(e) => onChange(e.target.value)}
            // A dropped link replaces what was there, instead of landing in the middle of it.
            onDrop={(e) => {
              if (disabled || e.dataTransfer.files.length > 0) return;
              const fromList = e.dataTransfer
                .getData("text/uri-list")
                .split(/\r?\n/)
                .find((line) => line.trim() !== "" && !line.startsWith("#"));
              const dropped = (fromList ?? e.dataTransfer.getData("text/plain")).trim();
              if (dropped) {
                e.preventDefault();
                e.stopPropagation();
                onChange(dropped);
              }
            }}
          />
        )}
        {(file || valid) && (
          <a className="wl-btn wl-btn--secondary wl-btn--sm" href={file ? file.link : link} target="_blank" rel="noopener noreferrer">
            {t("locations.attachmentOpen")}
          </a>
        )}
        {!disabled && (
          <label
            className="wl-btn wl-btn--secondary wl-btn--sm"
            style={{ whiteSpace: "nowrap" }}
            aria-disabled={busy !== null || undefined}
          >
            {file ? t("locations.attachmentReplace") : t("locations.attachmentChoose")}
            <input
              type="file"
              accept={ATTACHMENT_ACCEPT}
              disabled={busy !== null}
              style={{ display: "none" }}
              onChange={(e) => {
                put(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>
        )}
        {!disabled && file && (
          <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" disabled={busy !== null} onClick={remove}>
            {t("locations.attachmentRemove")}
          </button>
        )}
      </div>
      {problem ? (
        <span className="wl-field__error">{problem}</span>
      ) : (
        <span className="wl-field__help">
          {busy === "upload"
            ? t("locations.attachmentUploading")
            : busy === "remove"
              ? t("locations.attachmentRemoving")
              : invalid
                ? t("locations.attachmentInvalid")
                : file
                  ? t("locations.attachmentStored")
                  : help}
        </span>
      )}
    </div>
  );
}

function GeneralTab({ location, canEdit, isAdmin }: { location: LocationDetail; canEdit: boolean; isAdmin: boolean }) {
  const { t } = useTranslation();
  const { isPending, error, saved, save } = useCardSave(location.id);
  const [form, setForm] = useState({
    name: location.name,
    street: location.street ?? "",
    houseNumber: location.houseNumber ?? "",
    houseNumberAddition: location.houseNumberAddition ?? "",
    postalCode: location.postalCode ?? "",
    city: location.city,
    countryCode: location.countryCode,
    detail: location.detail ?? "",
    phone: location.phone ?? "",
    email: location.email ?? "",
    gln: location.gln ?? "",
    arrivalPopup: location.arrivalPopup ?? "",
    departurePopup: location.departurePopup ?? "",
    addressAttachment: location.addressAttachment ?? "",
    routeAttachment: location.routeAttachment ?? "",
  });
  // The address as the lookup wrote it, until it is saved or typed over; with where it lies, which WLX keeps (19-10-2026).
  const [tookOver, setTookOver] = useState(false);
  const [tookPosition, setTookPosition] = useState<{ lat: number; lon: number } | null>(null);

  useEffect(() => {
    if (saved) setTookOver(false);
  }, [saved]);

  const input = (key: keyof typeof form) => ({
    id: `location-${key}`,
    className: "wl-input",
    value: form[key],
    disabled: !canEdit,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
      if ((ADDRESS_FORM_KEYS as readonly string[]).includes(key)) setTookOver(false);
    },
  });

  // The pop-ups the driver sees: only an admin changes them (S37).
  const popup = (key: "arrivalPopup" | "departurePopup") => ({
    id: `location-${key}`,
    className: "wl-textarea",
    rows: 3,
    maxLength: 2000,
    value: form[key],
    disabled: !canEdit || !isAdmin,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => setForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  function takeOver(candidate: AddressCandidate) {
    setForm((prev) => ({
      ...prev,
      street: candidate.street ?? prev.street,
      houseNumber: candidate.houseNumber ?? prev.houseNumber,
      postalCode: candidate.postalCode ?? prev.postalCode,
      city: candidate.city ?? prev.city,
      countryCode: candidate.countryCode ?? prev.countryCode,
    }));
    setTookOver(true);
    setTookPosition(candidate.lat !== null && candidate.lng !== null ? { lat: candidate.lat, lon: candidate.lng } : null);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    save(() =>
      updateLocation(location.id, {
        name: form.name.trim(),
        street: textOrNull(form.street),
        houseNumber: textOrNull(form.houseNumber),
        houseNumberAddition: textOrNull(form.houseNumberAddition),
        postalCode: textOrNull(form.postalCode),
        city: form.city.trim(),
        countryCode: form.countryCode.trim().toUpperCase(),
        detail: textOrNull(form.detail),
        phone: textOrNull(form.phone),
        email: textOrNull(form.email),
        gln: textOrNull(form.gln),
        // For anyone but an admin the API refuses the pop-ups and the attachments (S37, S65), so they stay out.
        ...(isAdmin
          ? {
              arrivalPopup: textOrNull(form.arrivalPopup),
              departurePopup: textOrNull(form.departurePopup),
              addressAttachment: textOrNull(form.addressAttachment),
              routeAttachment: textOrNull(form.routeAttachment),
            }
          : {}),
        ...(tookOver ? { addressSource: "here" as const, ...(tookPosition ?? {}) } : {}),
      }),
    );
  }

  return (
    <form onSubmit={submit} className="wl-card" style={CARD_STYLE}>
      {location.fromTalis && <TalisNote />}
      <div style={FORM_GRID}>
        <Field id="location-name" label={t("locations.nameLabel")} span={4}>
          <input {...input("name")} required minLength={2} maxLength={200} />
        </Field>

        <SectionTitle>{t("locations.addressTitle")}</SectionTitle>
        {canEdit && (
          <div style={{ gridColumn: "1 / -1" }}>
            <AddressSearch id="location-address-search" onPick={takeOver} />
          </div>
        )}
        <Field id="location-street" label={t("locations.streetLabel")} span={4}>
          <input {...input("street")} maxLength={200} />
        </Field>
        <Field id="location-houseNumber" label={t("locations.houseNumberLabel")} span={1}>
          <input {...input("houseNumber")} maxLength={20} />
        </Field>
        <Field id="location-houseNumberAddition" label={t("locations.additionLabel")} span={1}>
          <input {...input("houseNumberAddition")} maxLength={20} />
        </Field>
        <Field id="location-postalCode" label={t("locations.postalCodeLabel")} span={1}>
          <input {...input("postalCode")} maxLength={20} />
        </Field>
        <Field id="location-city" label={t("locations.cityLabel")} span={4}>
          <input {...input("city")} required maxLength={100} />
        </Field>
        <Field id="location-countryCode" label={t("locations.countryLabel")} help={t("locations.countryHelp")} span={1}>
          <input {...input("countryCode")} required maxLength={2} style={{ textTransform: "uppercase" }} />
        </Field>
        <Field id="location-detail" label={t("locations.detailLabel")} help={t("locations.detailHelp")} span={4}>
          <input {...input("detail")} maxLength={500} />
        </Field>
        <div style={{ gridColumn: "1 / -1" }}>
          <AddressCheck location={location} canEdit={canEdit} tookOver={tookOver} onTakeOver={takeOver} />
        </div>

        <SectionTitle>{t("locations.contactTitle")}</SectionTitle>
        <Field id="location-phone" label={t("locations.phoneLabel")} span={2}>
          <input {...input("phone")} maxLength={40} />
        </Field>
        <Field id="location-email" label={t("locations.emailLabel")} help={t("locations.contactHelp")} span={2}>
          <input {...input("email")} type="email" maxLength={200} />
        </Field>
        <Field id="location-gln" label={t("locations.glnLabel")} help={t("locations.glnHelp")} span={2}>
          <input {...input("gln")} inputMode="numeric" maxLength={13} />
        </Field>

        <SectionTitle>{t("locations.boardComputerTitle")}</SectionTitle>
        <Field id="location-arrivalPopup" label={t("locations.arrivalPopupLabel")} help={t("locations.arrivalPopupHelp")} span={3}>
          <textarea {...popup("arrivalPopup")} />
        </Field>
        <Field id="location-departurePopup" label={t("locations.departurePopupLabel")} help={t("locations.departurePopupHelp")} span={3}>
          <textarea {...popup("departurePopup")} />
        </Field>
        {canEdit && !isAdmin && (
          <p className="wl-field__help" style={{ gridColumn: "1 / -1", margin: 0 }}>
            {t("locations.popupsAdminOnly")}
          </p>
        )}
        <div style={{ gridColumn: "span 3" }}>
          <AttachmentField
            id="location-addressAttachment"
            locationId={location.id}
            kind="address"
            label={t("locations.addressAttachmentLabel")}
            info={t("locations.addressAttachmentInfo")}
            help={t("locations.addressAttachmentHelp")}
            value={form.addressAttachment}
            file={location.attachmentFiles?.address ?? null}
            disabled={!canEdit || !isAdmin}
            onChange={(value) => setForm((prev) => ({ ...prev, addressAttachment: value }))}
          />
        </div>
        <div style={{ gridColumn: "span 3" }}>
          <AttachmentField
            id="location-routeAttachment"
            locationId={location.id}
            kind="route"
            label={t("locations.routeAttachmentLabel")}
            info={t("locations.routeAttachmentInfo")}
            help={t("locations.routeAttachmentHelp")}
            value={form.routeAttachment}
            file={location.attachmentFiles?.route ?? null}
            disabled={!canEdit || !isAdmin}
            onChange={(value) => setForm((prev) => ({ ...prev, routeAttachment: value }))}
          />
        </div>
      </div>

      <div className="wl-divider" style={{ margin: "20px 0 16px" }} />

      <div className="wl-grid-2">
        <div>
          <div className="t-caption" style={{ marginBottom: "6px" }}>{t("locations.positionTitle")}</div>
          <div className="t-body-sm t-mono">
            {location.lat !== null && location.lon !== null ? `${location.lat.toFixed(5)}, ${location.lon.toFixed(5)}` : "—"}
          </div>
          <span className="wl-field__help" style={{ display: "block", marginTop: "6px" }}>
            {t(location.positionSource === "here" ? "locations.positionFromLookup" : location.lat !== null ? "locations.positionHelp" : "locations.positionNone")}
          </span>
        </div>
        <div>
          <div className="t-caption" style={{ marginBottom: "6px" }}>{t("common.record")}</div>
          <div className="t-body-sm">{t("common.created")}: {formatDate(location.createdAt)}</div>
          <div className="t-body-sm">{t("common.lastUpdated")}: {formatDate(location.updatedAt)}</div>
          {location.talisPk && (
            <div className="t-body-sm">
              {t("locations.talisPkLabel")}: <span className="t-mono">{location.talisPk}</span>
            </div>
          )}
        </div>
      </div>

      <SaveBar canEdit={canEdit} isPending={isPending} saved={saved} error={error} />
    </form>
  );
}

// ── Openingstijden ────────────────────────────────────────────────────────────

// What HERE knows of the business at a place that already exists, on request (Camiel,
// 23-09-2026: "bij een location als je daar kijkt bij de openingstijden ze alsnog op kunnen
// zoeken"). The same proposal as beside a new address; the name is the one to look for, so a
// place called "HEMA DC" in WLX can still be found as HEMA.
function HoursLookup({ location, onTake, onClose }: { location: LocationDetail; onTake?: (hours: OpeningHours) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(location.name);
  const query: PlaceHoursQuery = {
    name,
    street: location.street,
    houseNumber: location.houseNumber,
    houseNumberAddition: location.houseNumberAddition,
    postalCode: location.postalCode,
    city: location.city,
    countryCode: location.countryCode,
    lat: location.lat,
    lng: location.lon,
  };
  return (
    <div
      className="wl-vstack"
      style={{
        gap: "var(--space-2)",
        alignItems: "stretch",
        marginBottom: "var(--space-4)",
        padding: "var(--space-3)",
        background: "var(--surface-base)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-sm)",
      }}
    >
      <div className="wl-hstack" style={{ gap: "var(--space-2)", alignItems: "flex-end" }}>
        <div style={{ flex: 1, maxWidth: "24rem" }}>
          <Field id="loc-here-name" label={t("locations.hereLookupName")}>
            <input id="loc-here-name" className="wl-input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>
        <span className="wl-spacer" />
        <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={onClose}>
          {t("common.close")}
        </button>
      </div>
      <HereHoursHint query={query} onTake={onTake} />
    </div>
  );
}

function HoursTab({ location, canEdit }: { location: LocationDetail; canEdit: boolean }) {
  const { t } = useTranslation();
  const { isPending, error, saved, save, setError } = useCardSave(location.id);
  const [draft, setDraft] = useState<HoursDraft>(() => draftFromHours(location.openingHours));
  const status = useQuery({ queryKey: addressKeys.status, queryFn: getAddressLookupStatus, staleTime: 5 * 60_000 });
  const [lookingUp, setLookingUp] = useState(false);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const result = hoursFromDraft(draft);
    if (!result.hours) {
      setError(t(PROBLEM_KEYS[result.problem] as never));
      return;
    }
    const hours = result.hours;
    save(() => saveOpeningHours(location.id, hours));
  }

  const checked = location.hoursCheckedAt
    ? t("locations.hoursCheckedOn", { date: formatDate(location.hoursCheckedAt) })
    : location.hoursSource === "import"
      ? t("locations.hoursFromTalis")
      : t("locations.hoursNeverChecked");

  return (
    <form onSubmit={submit} className="wl-card" style={CARD_STYLE}>
      {location.fromTalis && <TalisNote />}
      {lookingUp ? (
        <HoursLookup
          location={location}
          // Taking the proposal over fills the editor; saving stays the planner's own act.
          onTake={canEdit ? (hours) => setDraft(draftFromHours(hours)) : undefined}
          onClose={() => setLookingUp(false)}
        />
      ) : (
        status.data?.available && (
          <div style={{ marginBottom: "var(--space-3)" }}>
            <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => setLookingUp(true)}>
              {t("locations.hereLookup")}
            </button>
          </div>
        )
      )}
      <OpeningHoursEditor draft={draft} onChange={setDraft} disabled={!canEdit} />
      <div className="wl-divider" style={{ margin: "16px 0" }} />
      <span className="t-caption t-muted">{checked}</span>
      <SaveBar canEdit={canEdit} isPending={isPending} saved={saved} error={error} />
    </form>
  );
}

// ── Eisen en instructies ──────────────────────────────────────────────────────

function safeLink(link: string) {
  return /^https?:\/\//i.test(link) ? (
    <a href={link} target="_blank" rel="noopener noreferrer">{link}</a>
  ) : (
    <span>{link}</span>
  );
}

function PreNotificationBlock({ notification }: { notification: PreNotification }) {
  const { t } = useTranslation();
  const rows: { key: string; label: string; value: React.ReactNode }[] = [];
  if (notification.bookSlot) {
    rows.push({
      key: "bookSlot",
      label: t("locations.preBookSlot"),
      value: notification.bookSlotFor.length > 0 ? t("locations.preOnlyFor", { debtors: notification.bookSlotFor.join(", ") }) : t("locations.preAlways"),
    });
  }
  if (notification.bookSlotEmails.length > 0) {
    rows.push({ key: "bookSlotEmails", label: t("locations.preBookSlotEmails"), value: notification.bookSlotEmails.join(", ") });
  }
  if (notification.links.length > 0) {
    rows.push({
      key: "links",
      label: t("locations.preLinks"),
      value: (
        <span className="wl-vstack" style={{ gap: "2px", alignItems: "flex-start" }}>
          {notification.links.map((link) => (
            <Fragment key={link}>{safeLink(link)}</Fragment>
          ))}
        </span>
      ),
    });
  }
  if (notification.announceTrip) {
    rows.push({
      key: "announceTrip",
      label: t("locations.preAnnounceTrip"),
      value:
        notification.announceTripFor.length > 0 ? t("locations.preOnlyFor", { debtors: notification.announceTripFor.join(", ") }) : t("locations.preAlways"),
    });
  }
  if (notification.announceTripEmails.length > 0) {
    rows.push({ key: "announceTripEmails", label: t("locations.preAnnounceTripEmails"), value: notification.announceTripEmails.join(", ") });
  }
  const demands = [notification.plateRequired ? t("locations.prePlateRequired") : null, notification.etaRequired ? t("locations.preEtaRequired") : null].filter(
    Boolean,
  );
  if (demands.length > 0) rows.push({ key: "demands", label: t("locations.preDemands"), value: demands.join(", ") });
  if (notification.memo) {
    rows.push({ key: "memo", label: t("locations.preMemo"), value: <span style={{ whiteSpace: "pre-wrap" }}>{notification.memo}</span> });
  }

  return (
    <div style={{ marginTop: "20px" }}>
      <div className="t-caption" style={{ fontWeight: "var(--fw-semibold)", color: "var(--text-secondary)", marginBottom: "6px" }}>
        {t("locations.preNotificationTitle")}
      </div>
      <dl className="t-body-sm" style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 16px", margin: 0 }}>
        {rows.map((row) => (
          <Fragment key={row.key}>
            <dt className="t-muted">{row.label}</dt>
            <dd style={{ margin: 0 }}>{row.value}</dd>
          </Fragment>
        ))}
      </dl>
      <span className="wl-field__help" style={{ display: "block", marginTop: "6px" }}>{t("locations.preNotificationHelp")}</span>
    </div>
  );
}

function RequirementsTab({ location, canEdit }: { location: LocationDetail; canEdit: boolean }) {
  const { t } = useTranslation();
  const { isPending, error, saved, save } = useCardSave(location.id);
  const [requirements, setRequirements] = useState<string[]>(location.requirements);
  const [noBatchPicking, setNoBatchPicking] = useState(location.noBatchPicking ?? false);
  const [tripMarkRequired, setTripMarkRequired] = useState(location.tripMarkRequired ?? false);
  const [deliveryWeekdays, setDeliveryWeekdays] = useState<number[]>(location.deliveryWeekdays ?? []);
  // What the address decides is shown, not ticked (S29).
  const fixed = location.requirements.filter((requirement) => (ADDRESS_REQUIREMENTS as readonly string[]).includes(requirement));

  function submit(e: React.FormEvent) {
    e.preventDefault();
    save(() => updateLocation(location.id, { requirements, noBatchPicking, tripMarkRequired, deliveryWeekdays }));
  }

  return (
    <form onSubmit={submit} className="wl-card" style={CARD_STYLE}>
      {location.fromTalis && <TalisNote />}
      <div className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)" }}>{t("locations.requirementsTitle")}</div>
      <p className="wl-field__help" style={{ margin: "4px 0 12px" }}>{t("locations.requirementsHelp")}</p>
      <div className="wl-grid-3" style={{ gap: "8px 16px" }}>
        {LOCATION_REQUIREMENTS.filter((requirement) => !ADDRESS_REQUIREMENTS.includes(requirement)).map((requirement) => (
          <Check
            key={requirement}
            id={`requirement-${requirement}`}
            label={t(REQUIREMENT_LABEL_KEYS[requirement] as never)}
            checked={requirements.includes(requirement)}
            disabled={!canEdit}
            onChange={(checked) =>
              setRequirements((prev) => (checked ? [...prev, requirement] : prev.filter((existing) => existing !== requirement)))
            }
          />
        ))}
      </div>
      {fixed.map((requirement) => (
        <div key={requirement} className="wl-hstack" style={{ gap: "8px", marginTop: "10px", flexWrap: "wrap" }}>
          <span className="wl-chip">{t(requirementLabelKey(requirement) as never)}</span>
          <span className="t-caption t-muted">{t("locations.fcTrainingFixed")}</span>
        </div>
      ))}

      {location.preNotification && <PreNotificationBlock notification={location.preNotification} />}

      <div className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)", marginTop: "20px" }}>{t("locations.warehouseTitle")}</div>
      <p className="wl-field__help" style={{ margin: "4px 0 12px" }}>{t("locations.noBatchPickingHelp")}</p>
      <Check id="no-batch-picking" label={t("locations.noBatchPicking")} checked={noBatchPicking} disabled={!canEdit} onChange={setNoBatchPicking} />

      {/* The trip made in the work queue (docs/tms-orderintake.md §3i): Nörten-Hardenberg. */}
      <div className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)", marginTop: "20px" }}>{t("locations.orderEntryTitle")}</div>
      <p className="wl-field__help" style={{ margin: "4px 0 12px" }}>{t("locations.tripMarkRequiredHelp")}</p>
      <Check id="trip-mark-required" label={t("locations.tripMarkRequired")} checked={tripMarkRequired} disabled={!canEdit} onChange={setTripMarkRequired} />

      {/* Where Hager's colli that were not in go to (docs/tms-orderintake.md §3j): the next of these days. */}
      <div className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)", marginTop: "20px" }}>{t("locations.deliveryWeekdays")}</div>
      <p className="wl-field__help" style={{ margin: "4px 0 12px" }}>{t("locations.deliveryWeekdaysHelp")}</p>
      <div className="wl-hstack" style={{ gap: "8px 16px", flexWrap: "wrap" }}>
        {[1, 2, 3, 4, 5].map((day) => (
          <Check
            key={day}
            id={`delivery-weekday-${day}`}
            label={t(`locations.weekday${day}` as never)}
            checked={deliveryWeekdays.includes(day)}
            disabled={!canEdit}
            onChange={(checked) => setDeliveryWeekdays((prev) => (checked ? [...prev, day].sort((a, b) => a - b) : prev.filter((existing) => existing !== day)))}
          />
        ))}
      </div>

      <SaveBar canEdit={canEdit} isPending={isPending} saved={saved} error={error} />
    </form>
  );
}

// ── Op hetzelfde adres ────────────────────────────────────────────────────────

/**
 * The other locations at this address (Camiel, 23-09-2026: "DMV.VEG en WIL.VEG01 slaat nergens
 * meer op dat is toch talis rommel"). TALIS made one for every way a customer wrote an address;
 * one that is this place is joined into it, and becomes a way of writing it. Other companies at
 * the same address - a port terrain - stay: the planner decides, one by one.
 */
function SameAddress({ location, canEdit }: { location: LocationDetail; canEdit: boolean }) {
  const { t } = useTranslation();
  const { isPending, error, save } = useCardSave(location.id);
  const [asking, setAsking] = useState<string | null>(null);
  // The waiting orders a join let through (S62): said here, also once the last one went.
  const [ordersLinked, setOrdersLinked] = useState(0);
  if (location.sameAddress.length === 0 && ordersLinked === 0) return null;
  const other = location.sameAddress.find((place) => place.id === asking) ?? null;

  return (
    <div className="wl-card" style={{ ...CARD_STYLE, marginBottom: "16px" }}>
      <SectionTitle>{t("locations.sameAddressTitle")}</SectionTitle>
      <p className="wl-field__help" style={{ margin: "0 0 12px" }}>{t("locations.sameAddressHelp")}</p>
      {error && (
        <div className="wl-alert wl-alert--danger" style={{ marginBottom: "12px" }} role="alert">
          <span>{error}</span>
        </div>
      )}
      <div className="wl-table-wrap">
        <table className="wl-table">
          <tbody>
            {location.sameAddress.map((place) => (
              <tr key={place.id}>
                <td className="t-mono" style={{ width: "130px" }}>{place.code}</td>
                <td>
                  <Link to="/locations/$id" params={{ id: place.id }}>{place.name}</Link>
                  {!place.isActive && <span className="t-caption t-muted" style={{ marginLeft: "8px" }}>{t("common.inactive")}</span>}
                </td>
                <td className="t-caption t-muted" style={{ width: "140px" }}>{t("locations.activityCount", { count: place.activities })}</td>
                <td style={{ textAlign: "right", width: "200px" }}>
                  {canEdit && (
                    <button type="button" className="wl-btn wl-btn--secondary wl-btn--sm" disabled={isPending} onClick={() => setAsking(place.id)}>
                      {t("locations.mergeInto")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {other && (
        <div className="wl-alert wl-alert--warning" style={{ marginTop: "12px", flexDirection: "column", alignItems: "stretch", gap: "8px" }}>
          <span>{t("locations.mergeConfirm", { name: other.name, into: location.name })}</span>
          <div className="wl-hstack" style={{ gap: "8px" }}>
            <button
              type="button"
              className="wl-btn wl-btn--primary wl-btn--sm"
              disabled={isPending}
              onClick={() => {
                save(async () => {
                  const next = await mergeLocation(location.id, other.id);
                  setOrdersLinked(next.ordersLinked ?? 0);
                  return next;
                });
                setAsking(null);
              }}
            >
              {t("locations.mergeConfirmYes")}
            </button>
            <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" disabled={isPending} onClick={() => setAsking(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Aliassen ──────────────────────────────────────────────────────────────────

function AliasesTab({ location, canEdit }: { location: LocationDetail; canEdit: boolean }) {
  const { t } = useTranslation();
  const { isPending, error, save, setError } = useCardSave(location.id);
  const [kind, setKind] = useState<"code" | "text">("text");
  const [value, setValue] = useState("");

  function add(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setError(t("locations.aliasTooShort"));
      return;
    }
    save(async () => {
      const next = await addAlias(location.id, { kind, value: trimmed });
      setValue("");
      return next;
    });
  }

  return (
    <div className="wl-card" style={CARD_STYLE}>
      <p className="wl-field__help" style={{ margin: "0 0 12px" }}>{t("locations.aliasesHelp")}</p>
      {error && (
        <div className="wl-alert wl-alert--danger" style={{ marginBottom: "12px" }} role="alert">
          <span>{error}</span>
        </div>
      )}
      <div className="wl-table-wrap">
        <table className="wl-table">
          <thead>
            <tr>
              <th>{t("locations.colAliasKind")}</th>
              <th>{t("locations.colAliasValue")}</th>
              <th>{t("locations.colSource")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {location.aliases.length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: "20px" }}>
                  {t("locations.aliasesEmpty")}
                </td>
              </tr>
            )}
            {location.aliases.map((alias) => (
              <tr key={alias.id}>
                <td>
                  <span className="wl-chip">{t(ALIAS_KIND_LABEL_KEYS[alias.kind] as never)}</span>
                </td>
                <td>
                  <span className={alias.kind === "code" ? "t-mono" : undefined}>{alias.value}</span>
                  {alias.party && <div className="t-caption t-muted">{alias.party.name}</div>}
                </td>
                <td>
                  <span className="wl-chip">{t(alias.source === "import" ? "locations.sourceImport" : "locations.sourcePlanner")}</span>
                </td>
                <td style={{ textAlign: "right" }}>
                  {canEdit && alias.source === "planner" && (
                    <button
                      type="button"
                      className="wl-btn wl-btn--ghost wl-btn--sm"
                      disabled={isPending}
                      onClick={() => save(() => removeAlias(location.id, alias.id))}
                    >
                      {t("locations.aliasRemove")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <form onSubmit={add} className="wl-hstack" style={{ gap: "12px", marginTop: "16px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <Field id="alias-kind" label={t("locations.aliasKindLabel")}>
            <select id="alias-kind" className="wl-select" value={kind} onChange={(e) => setKind(e.target.value as "code" | "text")}>
              <option value="text">{t("locations.aliasKindText")}</option>
              <option value="code">{t("locations.aliasKindCode")}</option>
            </select>
          </Field>
          <Field id="alias-value" label={t("locations.aliasValueLabel")}>
            <input id="alias-value" className="wl-input" value={value} maxLength={200} onChange={(e) => setValue(e.target.value)} />
          </Field>
          <button type="submit" className="wl-btn wl-btn--secondary" disabled={isPending}>
            {t("locations.aliasAdd")}
          </button>
        </form>
      )}
    </div>
  );
}

// ── The card ──────────────────────────────────────────────────────────────────

interface LocationCardProps {
  location: LocationDetail;
  canEdit: boolean;
  /** Only an admin changes the pop-ups in the on-board computer (S37). */
  isAdmin: boolean;
  tab: LocationCardTab;
  onTab: (tab: LocationCardTab) => void;
}

export function LocationCard({ location, canEdit, isAdmin, tab, onTab }: LocationCardProps) {
  const { t } = useTranslation();
  useScreenLabel(location.name);
  const words = useHoursWords();
  const { isPending, error, save } = useCardSave(location.id);
  const active = LOCATION_CARD_TABS.includes(tab) ? tab : "general";

  return (
    <>
      <div style={{ marginBottom: "20px" }}>
        <BackLink list="/locations" listLabel={t("nav.locations")} style={{ marginBottom: "var(--space-2)" }} />
        <div className="wl-hstack" style={{ gap: "12px", alignItems: "center", marginBottom: "6px", flexWrap: "wrap" }}>
          <h1 className="t-h2" style={{ margin: 0 }}>{location.name}</h1>
          <span className={`wl-status ${location.isActive ? "wl-status--delivered" : "wl-status--cancelled"}`}>
            <span className="wl-status__dot" />
            {location.isActive ? t("common.active") : t("common.inactive")}
          </span>
          {location.warehouses.map((warehouse) => (
            <Link key={warehouse.id} to="/warehouse/locations" search={{ warehouse: warehouse.code }} className="wl-chip wl-chip--navy">
              {t("locations.warehouseChip", { name: warehouse.name })}
            </Link>
          ))}
          {location.requirements.map((requirement) => {
            const key = requirementLabelKey(requirement);
            return (
              <span key={requirement} className="wl-chip">
                {key ? t(key as never) : requirement}
              </span>
            );
          })}
          <div className="wl-spacer" />
          {/* Joined into another location: it stays inactive (locations.merge.ts). */}
          {canEdit && !location.mergedInto && (
            <button
              type="button"
              className="wl-btn wl-btn--secondary wl-btn--sm"
              disabled={isPending}
              onClick={() => save(() => updateLocation(location.id, { isActive: !location.isActive }))}
            >
              {location.isActive ? t("common.deactivate") : t("common.reactivate")}
            </button>
          )}
        </div>
        <div className="wl-hstack t-body-sm t-muted" style={{ gap: "12px", flexWrap: "wrap" }}>
          <span className="t-mono">{location.code}</span>
          <span>·</span>
          <span>{formatAddress(location)}</span>
          <span>·</span>
          <span>{describeOpeningHours(location.openingHours, words)}</span>
          {location.mergedInto && (
            <>
              <span>·</span>
              <span>
                {t("locations.mergedInto")}{" "}
                <Link to="/locations/$id" params={{ id: location.mergedInto.id }}>{location.mergedInto.name}</Link>
              </span>
            </>
          )}
          {location.sameAddress.length > 0 && (
            <>
              <span>·</span>
              <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" style={{ padding: 0, height: "auto" }} onClick={() => onTab("aliases")}>
                {t("locations.sameAddressNote", { count: location.sameAddress.length })}
              </button>
            </>
          )}
        </div>
        {error && (
          <div className="wl-alert wl-alert--danger" style={{ marginTop: "12px" }} role="alert">
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="wl-tabs" role="tablist" style={{ marginBottom: "16px" }}>
        {LOCATION_CARD_TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active === key}
            className={`wl-tab ${active === key ? "is-active" : ""}`}
            onClick={() => onTab(key)}
          >
            {t(TAB_LABEL_KEYS[key] as never)}
            {key === "aliases" && location.aliases.length > 0 && (
              <span className="t-caption t-muted" style={{ marginLeft: "6px", fontVariantNumeric: "tabular-nums" }}>{location.aliases.length}</span>
            )}
          </button>
        ))}
      </div>

      {active === "general" && <GeneralTab key={location.id} location={location} canEdit={canEdit} isAdmin={isAdmin} />}
      {active === "hours" && <HoursTab key={location.id} location={location} canEdit={canEdit} />}
      {active === "requirements" && <RequirementsTab key={location.id} location={location} canEdit={canEdit} />}
      {active === "aliases" && (
        <>
          <SameAddress key={`same-${location.id}`} location={location} canEdit={canEdit} />
          <AliasesTab key={location.id} location={location} canEdit={canEdit} />
        </>
      )}
    </>
  );
}
